import _extends from "@babel/runtime/helpers/esm/extends";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import { createElement } from "@wordpress/element";

/** @format */

/**
 * External dependencies
 */
import { __, sprintf } from '@wordpress/i18n';
import { Component, createRef, Fragment } from '@wordpress/element';
import classnames from 'classnames';
import { IconButton } from '@wordpress/components';
import { find, get, noop } from 'lodash';
import Gridicon from 'gridicons';
import PropTypes from 'prop-types';
import { withInstanceId } from '@wordpress/compose';
var ASC = 'asc';
var DESC = 'desc';

var getDisplay = function getDisplay(cell) {
  return cell.display || null;
};
/**
 * A table component, without the Card wrapper. This is a basic table display, sortable, but no default filtering.
 *
 * Row data should be passed to the component as a list of arrays, where each array is a row in the table.
 * Headers are passed in separately as an array of objects with column-related properties. For example,
 * this data would render the following table.
 *
 * ```js
 * const headers = [ { label: 'Month' }, { label: 'Orders' }, { label: 'Revenue' } ];
 * const rows = [
 * 	[
 * 		{ display: 'January', value: 1 },
 * 		{ display: 10, value: 10 },
 * 		{ display: '$530.00', value: 530 },
 * 	],
 * 	[
 * 		{ display: 'February', value: 2 },
 * 		{ display: 13, value: 13 },
 * 		{ display: '$675.00', value: 675 },
 * 	],
 * 	[
 * 		{ display: 'March', value: 3 },
 * 		{ display: 9, value: 9 },
 * 		{ display: '$460.00', value: 460 },
 * 	],
 * ]
 * ```
 *
 * |   Month  | Orders | Revenue |
 * | ---------|--------|---------|
 * | January  |     10 | $530.00 |
 * | February |     13 | $675.00 |
 * | March    |      9 | $460.00 |
 */


var Table =
/*#__PURE__*/
function (_Component) {
  _inherits(Table, _Component);

  function Table(props) {
    var _this;

    _classCallCheck(this, Table);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Table).call(this, props));
    _this.state = {
      tabIndex: null,
      isScrollable: false
    };
    _this.container = createRef();
    _this.sortBy = _this.sortBy.bind(_assertThisInitialized(_this));
    _this.updateTableShadow = _this.updateTableShadow.bind(_assertThisInitialized(_this));
    return _this;
  }

  _createClass(Table, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this$container$curre = this.container.current,
          scrollWidth = _this$container$curre.scrollWidth,
          clientWidth = _this$container$curre.clientWidth;
      var scrollable = scrollWidth > clientWidth;
      /* eslint-disable react/no-did-mount-set-state */

      this.setState({
        tabIndex: scrollable ? '0' : null
      });
      /* eslint-enable react/no-did-mount-set-state */

      this.updateTableShadow();
      window.addEventListener('resize', this.updateTableShadow);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      window.removeEventListener('resize', this.updateTableShadow);
    }
  }, {
    key: "sortBy",
    value: function sortBy(key) {
      var _this2 = this;

      var _this$props = this.props,
          headers = _this$props.headers,
          query = _this$props.query;
      return function () {
        var currentKey = query.orderby || get(find(headers, {
          defaultSort: true
        }), 'key', false);
        var currentDir = query.order || get(find(headers, {
          key: currentKey
        }), 'defaultOrder', DESC);
        var dir = DESC;

        if (key === currentKey) {
          dir = DESC === currentDir ? ASC : DESC;
        }

        _this2.props.onSort(key, dir);
      };
    }
  }, {
    key: "updateTableShadow",
    value: function updateTableShadow() {
      var table = this.container.current;
      var scrolledToEnd = table.scrollWidth - table.scrollLeft <= table.offsetWidth;
      this.setState({
        isScrollable: !scrolledToEnd
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      var _this$props2 = this.props,
          ariaHidden = _this$props2.ariaHidden,
          caption = _this$props2.caption,
          classNames = _this$props2.classNames,
          headers = _this$props2.headers,
          instanceId = _this$props2.instanceId,
          query = _this$props2.query,
          rowHeader = _this$props2.rowHeader,
          rows = _this$props2.rows;
      var _this$state = this.state,
          isScrollable = _this$state.isScrollable,
          tabIndex = _this$state.tabIndex;
      var classes = classnames('woocommerce-table__table', classNames, {
        'is-scrollable': isScrollable
      });
      var sortedBy = query.orderby || get(find(headers, {
        defaultSort: true
      }), 'key', false);
      var sortDir = query.order || get(find(headers, {
        key: sortedBy
      }), 'defaultOrder', DESC);
      var hasData = !!rows.length;
      return createElement("div", {
        className: classes,
        ref: this.container,
        tabIndex: tabIndex,
        "aria-hidden": ariaHidden,
        "aria-labelledby": "caption-".concat(instanceId),
        role: "group",
        onScroll: this.updateTableShadow
      }, createElement("table", null, createElement("caption", {
        id: "caption-".concat(instanceId),
        className: "woocommerce-table__caption screen-reader-text"
      }, caption, tabIndex === '0' && createElement("small", null, __('(scroll to see more)', 'woocommerce-admin'))), createElement("tbody", null, createElement("tr", null, headers.map(function (header, i) {
        var cellClassName = header.cellClassName,
            isLeftAligned = header.isLeftAligned,
            isSortable = header.isSortable,
            isNumeric = header.isNumeric,
            key = header.key,
            label = header.label,
            screenReaderLabel = header.screenReaderLabel;
        var labelId = "header-".concat(instanceId, "-").concat(i);
        var thProps = {
          className: classnames('woocommerce-table__header', cellClassName, {
            'is-left-aligned': isLeftAligned,
            'is-sortable': isSortable,
            'is-sorted': sortedBy === key,
            'is-numeric': isNumeric
          })
        };

        if (isSortable) {
          thProps['aria-sort'] = 'none';

          if (sortedBy === key) {
            thProps['aria-sort'] = sortDir === ASC ? 'ascending' : 'descending';
          }
        } // We only sort by ascending if the col is already sorted descending


        var iconLabel = sortedBy === key && sortDir !== ASC ? sprintf(__('Sort by %s in ascending order', 'woocommerce-admin'), screenReaderLabel) : sprintf(__('Sort by %s in descending order', 'woocommerce-admin'), screenReaderLabel);
        var textLabel = createElement(Fragment, null, createElement("span", {
          "aria-hidden": Boolean(screenReaderLabel)
        }, label), screenReaderLabel && createElement("span", {
          className: "screen-reader-text"
        }, screenReaderLabel));
        return createElement("th", _extends({
          role: "columnheader",
          scope: "col",
          key: i
        }, thProps), isSortable ? createElement(Fragment, null, createElement(IconButton, {
          icon: sortedBy === key && sortDir === ASC ? createElement(Gridicon, {
            size: 18,
            icon: "chevron-up"
          }) : createElement(Gridicon, {
            size: 18,
            icon: "chevron-down"
          }),
          "aria-describedby": labelId,
          onClick: hasData ? _this3.sortBy(key) : noop,
          isDefault: true
        }, textLabel), createElement("span", {
          className: "screen-reader-text",
          id: labelId
        }, iconLabel)) : textLabel);
      })), hasData ? rows.map(function (row, i) {
        return createElement("tr", {
          key: i
        }, row.map(function (cell, j) {
          var _headers$j = headers[j],
              cellClassName = _headers$j.cellClassName,
              isLeftAligned = _headers$j.isLeftAligned,
              isNumeric = _headers$j.isNumeric;
          var isHeader = rowHeader === j;
          var Cell = isHeader ? 'th' : 'td';
          var cellClasses = classnames('woocommerce-table__item', cellClassName, {
            'is-left-aligned': isLeftAligned,
            'is-numeric': isNumeric,
            'is-sorted': sortedBy === headers[j].key
          });
          return createElement(Cell, {
            scope: isHeader ? 'row' : null,
            key: j,
            className: cellClasses
          }, getDisplay(cell));
        }));
      }) : createElement("tr", null, createElement("td", {
        className: "woocommerce-table__empty-item",
        colSpan: headers.length
      }, __('No data to display', 'woocommerce-admin'))))));
    }
  }]);

  return Table;
}(Component);

Table.propTypes = {
  /**
   * Controls whether this component is hidden from screen readers. Used by the loading state, before there is data to read.
   * Don't use this on real tables unless the table data is loaded elsewhere on the page.
   */
  ariaHidden: PropTypes.bool,

  /**
   * A label for the content in this table
   */
  caption: PropTypes.string.isRequired,

  /**
   * Additional CSS classes.
   */
  className: PropTypes.string,

  /**
   * An array of column headers, as objects.
   */
  headers: PropTypes.arrayOf(PropTypes.shape({
    /**
     * Boolean, true if this column is the default for sorting. Only one column should have this set.
     */
    defaultSort: PropTypes.bool,

    /**
     * String, asc|desc if this column is the default for sorting. Only one column should have this set.
     */
    defaultOrder: PropTypes.string,

    /**
     * Boolean, true if this column should be aligned to the left.
     */
    isLeftAligned: PropTypes.bool,

    /**
     * Boolean, true if this column is a number value.
     */
    isNumeric: PropTypes.bool,

    /**
     * Boolean, true if this column is sortable.
     */
    isSortable: PropTypes.bool,

    /**
     * The API parameter name for this column, passed to `orderby` when sorting via API.
     */
    key: PropTypes.string,

    /**
     * The display label for this column.
     */
    label: PropTypes.node,

    /**
     * Boolean, true if this column should always display in the table (not shown in toggle-able list).
     */
    required: PropTypes.bool,

    /**
     * The label used for screen readers for this column.
     */
    screenReaderLabel: PropTypes.string
  })),

  /**
   * A function called when sortable table headers are clicked, gets the `header.key` as argument.
   */
  onSort: PropTypes.func,

  /**
   * The query string represented in object form
   */
  query: PropTypes.object,

  /**
   * An array of arrays of display/value object pairs.
   */
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
    /**
     * Display value, used for rendering- strings or elements are best here.
     */
    display: PropTypes.node,

    /**
     * "Real" value used for sorting, and should be a string or number. A column with `false` value will not be sortable.
     */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
  }))).isRequired,

  /**
   * Which column should be the row header, defaults to the first item (`0`) (but could be set to `1`, if the first col
   * is checkboxes, for example). Set to false to disable row headers.
   */
  rowHeader: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
};
Table.defaultProps = {
  ariaHidden: false,
  headers: [],
  onSort: noop,
  query: {},
  rowHeader: 0
};
export default withInstanceId(Table);
//# sourceMappingURL=table.js.map